"use client";

import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";
import dynamic from "next/dynamic";
import DashboardHeader from "@/components/headers/DashboardHeader";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ToolInfo } from "@/contexts/ToolsContext";
import { useCreditsContext } from "@/contexts/CreditsContext";
import { trackFunnel } from "@/libs/analytics";
import { EditorV2HealthProvider } from "@/libs/editor-v2-health-analytics";
import GlobalEditorProvider from "@/app/editor/_providers/GlobalEditorProvider";
import ToolsSidebarV2 from "./ToolsSidebarV2";
import MyPhotosButton from "./MyPhotosButton";

const ToolLoadingFallback = () => (
  <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-50 z-50">
    <LoadingSpinner size="lg" text="Loading tool..." fullScreen={false} />
  </div>
);

function loadAiReportTool(toolId: string) {
  return dynamic(
    () =>
      import("@/app/editor/tools/ai-report").then((mod) => ({
        default: mod.createAiReportTool(toolId),
      })),
    { loading: ToolLoadingFallback }
  );
}

const AI_REPORT_IDS = [
  "attractiveness-test",
  "glow-up-plan",
  "skin-analysis",
  "color-analysis",
  "hair-analysis",
  "face-shape",
  "golden-ratio",
  "celebrity-lookalike",
  "face-reading",
  "age-estimate",
  "vibe-analysis",
  "dating-profile-score",
  "aura-reading",
] as const;

export const editorV2ConfigComponents: Record<string, any> = {
  "ai-avatars": dynamic(() => import("@/app/editor/tools/ai-avatars-v2"), {
    loading: ToolLoadingFallback,
  }),
  "ai-headshots": dynamic(() => import("@/app/editor/tools/ai-headshots-v2"), {
    loading: ToolLoadingFallback,
  }),
  "ai-photo-editor": dynamic(() => import("@/app/editor/tools/ai-photo-editor"), {
    loading: ToolLoadingFallback,
  }),
  "ai-designer": dynamic(() => import("@/app/editor/tools/ai-designer"), {
    loading: ToolLoadingFallback,
  }),
  ...Object.fromEntries(AI_REPORT_IDS.map((id) => [id, loadAiReportTool(id)])),
};

export default function EditorV2Shell({
  toolInfo,
  title,
  catalogToolId,
}: {
  toolInfo: ToolInfo;
  title?: string;
  /** Photo-editor preset id. Studios omit this and health events use tool_id. */
  catalogToolId?: string;
}) {
  const { credits, isLoading: creditsLoading } = useCreditsContext();
  const ConfigComponent = editorV2ConfigComponents[toolInfo.id];
  const [isDesktop, setIsDesktop] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [creditsLoading]);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!creditsLoading && credits !== null) {
      trackFunnel("tool_engagement", "tool_loaded", {
        tool_id: toolInfo.id,
        ...(catalogToolId ? { catalog_tool_id: catalogToolId } : {}),
        tool_name: title || toolInfo.title,
        credits_required: toolInfo.creditCost,
        user_credits: credits,
        editor_version: "v2",
      });
    }
  }, [creditsLoading, credits, toolInfo.id, toolInfo.title, toolInfo.creditCost, title, catalogToolId]);

  useEffect(() => {
    if (!isDesktop) return;

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isDesktop]);

  if (creditsLoading) {
    return <LoadingSpinner fullScreen text="Loading editor..." />;
  }

  return (
    <EditorV2HealthProvider
      toolId={toolInfo.id}
      catalogToolId={catalogToolId}
      toolName={title || toolInfo.title}
    >
      <GlobalEditorProvider toolInfo={toolInfo}>
        <div className="editor-app-height flex flex-col overflow-hidden bg-gray-50">
          <DashboardHeader variant="editor" title={title || toolInfo.title} extraActions={<MyPhotosButton />} />

          <div className="flex flex-col md:flex-row flex-1 min-h-0 relative overflow-hidden">
            <ToolsSidebarV2 currentToolId={catalogToolId ?? toolInfo.id} className="hidden md:block" />

            <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
              <Suspense fallback={<div>Loading...</div>}>
                <ConfigComponent />
              </Suspense>
            </div>
          </div>
        </div>
      </GlobalEditorProvider>
    </EditorV2HealthProvider>
  );
}
